/**
 * Shared Cart Vuex module.
 * Calls /owa/shared-cart-sessions endpoints.
 *
 * This is for dine-in table ordering where every guest edits the same shared
 * RestaurantOrder instead of host/participant carts.
 */

import Vue from 'vue'
import { SharedCartWebSocketService } from '@/services/shared-cart-ws.service'

var _wsInstance = null

// Session IDs confirmed dead (terminal order) this page load. fetchShoppingCart
// re-reads ?sc=<id> from the URL on every call and will happily re-join the
// same dead session over and over if the URL hasn't been cleaned up yet —
// stripping the URL query is async (router.replace) and can lose a race
// against another fetchShoppingCart call reading the stale query first. This
// set is the hard stop: once a sessionId is known dead, configureSession
// refuses to rejoin it regardless of what the URL still says.
var _knownDeadSessionIds = new Set()

// refetchAndMerge is dispatched independently from several bootstrap paths on
// a single page load (router guards, fetchShoppingCart, the explicit ?sc=
// join) that can all fire before the first call's GET resolves. Without this,
// each one races in and issues its own duplicate request for the same
// sessionId. Sharing the in-flight promise collapses concurrent callers into
// one actual network call.
var _inFlightRefetch = null

var LS_KEYS = {
  sessionId: 'sc_sessionId',
  storeId: 'sc_storeId',
  tableId: 'sc_tableId'
}

function lsGet(key) {
  try { return window.localStorage.getItem(key) } catch (e) { return null }
}
function lsSet(key, value) {
  try { window.localStorage.setItem(key, value) } catch (e) { /* quota */ }
}
function lsRemove(key) {
  try { window.localStorage.removeItem(key) } catch (e) { /* noop */ }
}
function persistSession(sessionId, storeId, tableId) {
  if (sessionId) lsSet(LS_KEYS.sessionId, sessionId)
  if (storeId) lsSet(LS_KEYS.storeId, storeId)
  if (tableId) lsSet(LS_KEYS.tableId, tableId)
}
function clearPersistedSession() {
  Object.values(LS_KEYS).forEach(lsRemove)
}

function getOrderFromResponse(data) {
  if (!data) return null
  return data.order || data.restaurantOrder || data.cart || data
}

function getSessionIdFromResponse(data) {
  if (!data) return null
  return data.sessionId || data.id || data.uuid || null
}

// Backend throws this 409 (distinct from an optimistic-lock version conflict)
// once the session has moved out of ACTIVE — finalized or cancelled by
// another device. Retrying with a refetched version can never succeed.
function isSessionClosedError(error) {
  return !!(error && error.response && error.response.status === 409 &&
    error.response.data && error.response.data.errorCode === 'SHARED_CART_SESSION_CLOSED')
}

// Backend throws this 409 when the specific item being edited/removed was
// already changed by someone else (expectedItemVersion mismatch). Per the
// backend's own mutate() contract, this is terminal for that action — the
// server already retries once internally and gives up rather than merge
// conflicting edits to the same item, so the client must not retry blindly
// with the same (now-stale) expectedItemVersion; surface it and let the
// already-in-flight refetch show the current state instead.
function isItemVersionConflictError(error) {
  return !!(error && error.response && error.response.status === 409 &&
    error.response.data && error.response.data.errorCode === 'SHARED_CART_ITEM_VERSION_CONFLICT')
}

// These two codes mean a refetch-and-retry can genuinely succeed: the caller
// raced another guest's write and lost the CAS on a version token that
// changes with every write (cartVersion for fire/finalize, draftRoundId
// internally for mutate — surfaced to the client as DRAFT_CHANGED). Other
// 409 codes (SHARED_CART_REPAIR_NEEDED, SHARED_CART_ACTION_INVALID, etc.)
// describe a state a blind retry can't fix — retrying just wastes a
// round-trip before falling through to the same error anyway.
function isRetryableConflictError(error) {
  if (!(error && error.response && error.response.status === 409 && error.response.data)) return false
  var code = error.response.data.errorCode
  return code === 'SHARED_CART_VERSION_CONFLICT' || code === 'SHARED_CART_DRAFT_CHANGED'
}

// mutateCart is called from six+ places (increment, decrement, addToCart's
// ADD/EDIT/merge paths) that previously only console.error'd on failure —
// the button visibly did nothing with no explanation. Centralize the
// user-facing snack here so every caller gets it for free.
function showMutateCartErrorSnack(store, error) {
  var vuetify = Vue.prototype.$vuetify
  if (!vuetify) return
  var key = isItemVersionConflictError(error)
    ? '$vuetify.sharedCart.errorItemChanged'
    : isSessionClosedError(error)
      ? '$vuetify.sharedCart.errorSessionClosed'
      : '$vuetify.sharedCart.errorMutateCart'
  store.dispatch('showSnack', { text: vuetify.t(key), color: 'error', timeout: 4000 })
}

// Regression (confirmed against a real SharedCartSession Mongo doc): the backend DOES
// serialize a top-level session-status field (sessionStatus: "COMPLETED" in the DB,
// read client-side via responseData.status || responseData.sessionStatus in SET_SESSION
// below) -- the assumption this used to be checked on ("never carries a session-status
// field on any endpoint") was wrong. order.orderStatus alone is not reliable: some
// closeout flows (e.g. pay-at-counter/cash) never flip the nested order's own status
// away from UNPAID even after the session is genuinely COMPLETED server-side, so gating
// purely on order.orderStatus silently never detects the terminal session -- the client
// keeps re-adopting/showing a dead session ("reconnected to shared table" on refresh)
// and never shows the paid/closed dialog. Check both signals:
//   - order.orderStatus: PAY_REQUEST (finalizeSession's isBillAtPOS) or PAID
//   - sessionStatus (top-level): COMPLETED, CANCELLED, or PENDING (the session entity's own
//     state, independent of whatever the nested order object says)
// PENDING is a fallback backend added for when the finalize WS event doesn't fire cleanly --
// per backend, treat it as equivalent to COMPLETED (the table is still considered paid).
function isOrderTerminal(order, sessionStatus) {
  var status = order && order.orderStatus
  if (status === 'PAY_REQUEST' || status === 'PAID' || status === 'COMPLETED') return true
  return sessionStatus === 'COMPLETED' || sessionStatus === 'CANCELLED' || sessionStatus === 'PENDING'
}

// App.vue's sessionClosedNotice watcher only recognizes PAY_REQUEST/PAID/COMPLETED --
// order.orderStatus || sessionStatus alone would pass through values like 'UNPAID' or
// 'PENDING' unchanged (both truthy, so `||` never falls back), and the watcher would
// show neither the paid nor the pay-at-register dialog. isOrderTerminal already proved
// the session is over; only trust orderStatus/sessionStatus when it's a value the
// watcher understands, otherwise default to COMPLETED (mirrors the same fix in
// shared-cart-ws.service.js's SESSION_COMPLETED handler).
var TERMINAL_ORDER_STATUSES = ['PAY_REQUEST', 'PAID', 'COMPLETED']
function terminalNoticeOrderStatus(order, sessionStatus) {
  var raw = (order && order.orderStatus) || sessionStatus
  return TERMINAL_ORDER_STATUSES.indexOf(raw) !== -1 ? raw : 'COMPLETED'
}

function getItemsFromOrder(order) {
  var items = []
  if (!order || !order.subOrderList) return items
  order.subOrderList.forEach(function(subOrder) {
    if (subOrder.orderItemList) {
      items = items.concat(subOrder.orderItemList)
    }
  })
  return items
}

// Only items still in the draft (unfired) round — safe to merge/edit. NEW is
// the only pre-fire status; a fired sub-order advances through SUBMITTED ->
// IN_PROCESS -> ACCEPTED -> READY -> COMPLETED (see SUB_ORDER_STATUS in
// OrderEnums.java) as the kitchen/POS processes it, so excluding only
// SUBMITTED stopped working the moment a round moved past that first status
// -- e.g. an ACCEPTED round was incorrectly still treated as editable draft.
// Once sent, mutating those items by uuid fails anyway since the backend has
// since re-versioned them.
function getDraftItemsFromOrder(order) {
  var items = []
  if (!order || !order.subOrderList) return items
  order.subOrderList.forEach(function(subOrder) {
    if (subOrder.orderStatus === 'NEW' && subOrder.orderItemList) {
      items = items.concat(subOrder.orderItemList)
    }
  })
  return items
}

// DEV-11531: first validation error anywhere in the draft (unsent) round -- on an item, a combo
// child, or a modifier (an out-of-stock modifier carries ORD_ERR_0006 on the value, not the item).
// Already-sent rounds are ignored: an item that sold out after it was sent must not block the next.
function firstDraftError(order) {
  var found = null
  function visit(node) {
    if (!node || found) return
    if (Array.isArray(node.errors) && node.errors.length) {
      found = node.errors[0]
      return
    }
    (node.itemAttributeList || []).forEach(function(attribute) {
      visit(attribute);
      ((attribute && attribute.attrValueList) || []).forEach(visit)
    });
    (node.childItemList || []).forEach(visit)
  }
  getDraftItemsFromOrder(order).forEach(visit)
  return found
}

// DEV-11531: the backend never validates a round when it fires/finalizes it (the round is already
// marked accepted when it's priced), so an item marked out of stock after the guest opened the page
// went straight to the kitchen. Every refetch, though, returns the draft already validated -- refresh
// right before sending and refuse while any draft item has an error. Best-effort like group order's
// ready/checkout: if the refresh itself fails, send anyway rather than block on a network blip.
// The thrown error is shaped like an API error so callers' existing errorMsg handling shows it.
// ponytail: client-side gate -- a status change in the instant between refresh and send still gets
// through; the backend should also validate the draft before marking it accepted.
async function refuseInvalidDraft(dispatch, state) {
  try {
    await dispatch('refetchAndMerge')
  } catch (e) {
    return
  }
  var error = firstDraftError(state.order)
  if (!error) return
  var refused = new Error(error.errorMsg || 'Shared cart has invalid items')
  refused.response = { data: { errorCode: error.errorCode || 'ORD_ERR_INVALID_CART', errorMsg: error.errorMsg } }
  throw refused
}

// The refresh in refuseInvalidDraft can discover the session already ended (it clears it and records
// the notice). Reject exactly like the server's fire/finalize would have -- a 409
// SHARED_CART_SESSION_CLOSED -- so callers keep their existing handling. Resolving null instead would
// make payOrder's finalize path treat it as a successful order.
function sessionClosedError() {
  var t = Vue.prototype.$vuetify && Vue.prototype.$vuetify.t
  var msg = t ? t('$vuetify.sharedCart.errorSessionClosed') : undefined
  var closed = new Error(msg || 'This table has already been closed.')
  closed.response = { status: 409, data: { errorCode: 'SHARED_CART_SESSION_CLOSED', errorMsg: msg } }
  return closed
}

function syncOrderToCartStore(store, order) {
  if (!order) return
  store.commit('SET_SERVER_CART', order)
  // Deliberately NOT committing SET_SUB_ORDER here: that populates
  // state.multiOrder, which getExistingOrder exposes to unrelated features
  // (loyalty rewards' canAfford, HeaderBar's QR-scanner visibility, the
  // dine-in multi-suborder submission flow) that all assume it means "past
  // completed orders with redemption history" for this table — not a live
  // shared-cart order. Cart.vue already renders shared cart from
  // getServerCart/sharedCart getters directly.
  var serviceCharges = order.serviceCharges || []
  store.commit('SET_SERVICE_CHARGES', serviceCharges)
  store.dispatch('mergeServerWithLocalCart').catch(function() {})
}

async function doRefetchAndMerge({ commit, state, dispatch }) {
  commit('SET_ERROR', null)
  try {
    var res = await this.$api.get('/shared-cart-sessions/' + state.sessionId)
    var responseData = res.data || {}
    var order = getOrderFromResponse(responseData)
    var preserveUuids = state.editingItemUuids || []
    var staleUuids = []
    preserveUuids.forEach(function(uuid) {
      var serverItem = getItemsFromOrder(order).find(function(item) { return item.uuid === uuid })
      if (serverItem) staleUuids.push(uuid)
    })
    if (staleUuids.length) {
      commit('MARK_STALE_ITEM_UUIDS', staleUuids)
    }
    var sessionStatus = responseData.status || responseData.sessionStatus
    commit('SET_SESSION', {
      sessionId: getSessionIdFromResponse(responseData) || state.sessionId,
      storeId: responseData.storeId || state.storeId,
      tableId: responseData.tableId || responseData.tableNo || state.tableId,
      status: sessionStatus ||
        (isOrderTerminal(order, sessionStatus) ? 'COMPLETED' : state.status),
      cartVersion: responseData.cartVersion,
      order: order
    })
    syncOrderToCartStore(this, order)
    if (isOrderTerminal(order, sessionStatus)) {
      // The table has already been billed at POS, or completed by the
      // merchant directly — this device just hasn't been told yet (no live
      // WS event, e.g. a page load against an already-closed session).
      // Record the notice BEFORE clearing: clearSharedCart resets session
      // state in the same tick, so a component watching transient state
      // like `order`/`status` can lose the terminal order before Vue's
      // watcher queue flushes. sessionClosedNotice survives the reset.
      commit('SET_SESSION_CLOSED_NOTICE', { orderStatus: terminalNoticeOrderStatus(order, sessionStatus) })
      _knownDeadSessionIds.add(state.sessionId)
      await dispatch('clearSharedCart')
    } else if (!state.wsConnected) {
      // Confirmed alive — safe to open the realtime channel now.
      dispatch('connectWs', {})
    }
    return responseData
  } catch (error) {
    commit('SET_ERROR', error)
    throw error
  }
}

function state() {
  return {
    sessionId: null,
    storeId: null,
    tableId: null,
    order: null,
    status: null,
    cartVersion: null,
    wsConnected: false,
    isLoading: false,
    error: null,
    cancelled: false,
    staleItemUuids: [],
    editingItemUuids: [],
    justJoined: false,
    // Set once by refetchAndMerge/mutateCart/submitRound/finalizeSession when
    // they discover — via a successful refetch or a SHARED_CART_SESSION_CLOSED
    // 409 — that the session had already ended before this action ran (no live
    // WS event told us). Deliberately NOT cleared by RESET_SHARED_CART/
    // clearSharedCart, since those run in the same tick and would wipe it
    // before App.vue's watcher ever observes it. App.vue clears it explicitly
    // after showing the "this table's session has ended" dialog.
    sessionClosedNotice: null
  }
}

var getters = {
  isActive: function(state) {
    return !!(state.sessionId && state.storeId && state.tableId)
  },
  // DEV-11545: what guests call this table -- its name (B1) when the cart's table is this
  // shared table, else its id (4). The name comes from the store's tables and is persisted
  // with the cart's table, so it's there right after a reload, before the store reloads.
  tableLabel: function(state, getters, rootState) {
    var cart = rootState.cart || {}
    var sameTable = cart.tableDisplay && String(cart.tableId) === String(state.tableId)
    return (sameTable ? cart.tableDisplay : state.tableId) || ''
  },
  order: function(state) {
    return state.order
  },
  items: function(state) {
    return getItemsFromOrder(state.order)
  },
  draftItems: function(state) {
    return getDraftItemsFromOrder(state.order)
  },
  isItemStale: function(state) {
    return function(uuid) {
      return state.staleItemUuids.indexOf(uuid) !== -1
    }
  }
}

var mutations = {
  SET_SESSION: function(state, payload) {
    payload = payload || {}
    state.sessionId = payload.sessionId || state.sessionId
    state.storeId = payload.storeId || state.storeId
    state.tableId = payload.tableId || state.tableId
    state.status = payload.status || state.status
    if (payload.order) {
      state.order = payload.order
    }
    // cartVersion is a top-level field on SharedCartResponse, not nested under `order`
    if (payload.cartVersion !== undefined && payload.cartVersion !== null) {
      state.cartVersion = payload.cartVersion
    }
  },
  SET_SESSION_ID: function(state, sessionId) {
    state.sessionId = sessionId
  },
  SET_ORDER: function(state, order) {
    state.order = order
  },
  SET_CART_VERSION: function(state, cartVersion) {
    state.cartVersion = cartVersion
  },
  SET_STATUS: function(state, status) {
    state.status = status
  },
  SET_WS_CONNECTED: function(state, connected) {
    state.wsConnected = connected
  },
  SET_LOADING: function(state, loading) {
    state.isLoading = loading
  },
  SET_ERROR: function(state, error) {
    state.error = error
  },
  SET_CANCELLED: function(state, cancelled) {
    state.cancelled = cancelled
  },
  SET_JUST_JOINED: function(state, justJoined) {
    state.justJoined = !!justJoined
  },
  SET_SESSION_CLOSED_NOTICE: function(state, notice) {
    state.sessionClosedNotice = notice || null
  },
  SET_EDITING_ITEM_UUIDS: function(state, uuids) {
    state.editingItemUuids = uuids || []
  },
  MARK_STALE_ITEM_UUIDS: function(state, uuids) {
    state.staleItemUuids = Array.from(new Set([].concat(state.staleItemUuids, uuids || [])))
  },
  CLEAR_STALE_ITEM_UUIDS: function(state, uuids) {
    if (!uuids) {
      state.staleItemUuids = []
      return
    }
    state.staleItemUuids = state.staleItemUuids.filter(function(uuid) {
      return uuids.indexOf(uuid) === -1
    })
  },
  RESET_SHARED_CART: function(state) {
    state.sessionId = null
    state.storeId = null
    state.tableId = null
    state.order = null
    state.status = null
    state.cartVersion = null
    state.wsConnected = false
    state.isLoading = false
    state.error = null
    state.cancelled = false
    state.staleItemUuids = []
    state.editingItemUuids = []
    state.justJoined = false
  }
}

var actions = {
  startSession: async function({ commit, dispatch }, payload) {
    payload = payload || {}
    commit('RESET_SHARED_CART')
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      var params = { tableId: payload.tableId }
      if (payload.vc) params.vc = payload.vc
      var res = await this.$api.get('/shared-cart-sessions', { params: params })
      var responseData = res.data || {}
      var order = getOrderFromResponse(responseData)
      var sessionId = getSessionIdFromResponse(responseData)
      var sessionStatus = responseData.status || responseData.sessionStatus
      // The getOrCreate endpoint can still return an already-paid/completed
      // session for this table if the backend hasn't released the table for a
      // fresh session yet -- e.g. a bare fetchShoppingCart() re-triggering this
      // dine-in auto-join right after POS payment. Unlike doRefetchAndMerge,
      // this path had no terminal-status check at all, so it would silently
      // re-adopt the dead session's order and reconnect the WS: the guest's
      // page would keep showing the paid-off "Shared Table" panel indefinitely,
      // with nothing (no refresh, no timer) ever re-checking it again. Checking
      // sessionStatus too (not just order.orderStatus) matters here specifically --
      // this is the "reconnected to shared table" symptom on refresh, confirmed
      // against a real session whose order never carried a terminal orderStatus
      // but whose sessionStatus was genuinely COMPLETED server-side.
      if (isOrderTerminal(order, sessionStatus)) {
        // DEV-11506: guests on a table URL now stay at the table and refetch after the
        // "session ended" dialog. Only notify once per session, so a backend that keeps
        // handing back the same finished session can't trap them in a dialog loop.
        if (!(sessionId && _knownDeadSessionIds.has(sessionId))) {
          commit('SET_SESSION_CLOSED_NOTICE', { orderStatus: terminalNoticeOrderStatus(order, sessionStatus) })
        }
        if (sessionId) _knownDeadSessionIds.add(sessionId)
        await dispatch('clearSharedCart')
        return responseData
      }
      commit('SET_SESSION', {
        sessionId: sessionId,
        storeId: payload.storeId || responseData.storeId,
        tableId: payload.tableId || responseData.tableId || responseData.tableNo,
        status: responseData.status || responseData.sessionStatus,
        cartVersion: responseData.cartVersion,
        order: order
      })
      persistSession(sessionId, payload.storeId || responseData.storeId, payload.tableId || responseData.tableId || responseData.tableNo)
      // Drop any local-only cart items left over from before this shared
      // session started — they don't belong to the shared order (subOrderList
      // is server-side truth) and mergeServerWithLocalCart only reconciles
      // items against a live order, so orphaned local items would otherwise
      // sit invisibly in getLocalCart forever, blocking the empty-cart UI.
      commit('RESET_CART', { storeId: null, tempSlots: {} }, { root: true })
      syncOrderToCartStore(this, order)
      dispatch('connectWs', payload.wsHandlers || {})
      return responseData
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  configureSession: function({ commit, dispatch }, payload) {
    payload = payload || {}
    if (payload.sessionId && _knownDeadSessionIds.has(payload.sessionId)) {
      return
    }
    commit('SET_SESSION', payload)
    persistSession(payload.sessionId, payload.storeId, payload.tableId)
    // See startSession — drop stale local-only cart items before syncing so
    // they don't linger and block the empty-cart UI for this shared session.
    commit('RESET_CART', { storeId: null, tempSlots: {} }, { root: true })
    if (payload.order) {
      syncOrderToCartStore(this, payload.order)
    }
    // Deliberately NOT connecting WS here — the session may already be dead
    // (stale ?sc= link, table closed while we were away). refetchAndMerge
    // (below) confirms liveness first and connects WS only once it knows the
    // session is still ACTIVE.
  },

  refetchAndMerge: function(context) {
    if (!context.state.sessionId) return Promise.resolve(null)
    if (_inFlightRefetch) return _inFlightRefetch
    var self = this
    _inFlightRefetch = doRefetchAndMerge.call(self, context).finally(function() {
      _inFlightRefetch = null
    })
    return _inFlightRefetch
  },

  mutateCart: async function({ commit, state, dispatch }, payload) {
    payload = payload || {}
    if (!state.sessionId) return null
    commit('SET_ERROR', null)
    var mutateActions = (payload.operations || []).map(function(op) {
      var action = Object.assign({}, op)
      if (op.type === 'ADD') action.type = 'ITEM_ADD'
      else if (op.type === 'REMOVE') action.type = 'ITEM_REMOVE'
      else if (op.type === 'EDIT') action.type = 'ITEM_EDIT'
      return action
    })
    if (payload.specialRequest !== undefined) {
      mutateActions.push({ type: 'SET_SPECIAL_REQUEST', specialRequest: payload.specialRequest })
    }
    // Dynamic QR code stores validate `vc` (the code's one-time/rotating verification
    // token) on every write against the session, not just at startSession -- matches
    // cart.js's own read-it-live-from-the-URL pattern for the regular cart flow.
    // Without this, any table entered via a dynamic QR link (scan-table route) got
    // ORD_ERR_INVALID_DYNAMIC_QRCODE_VC on every mutation/fire/finalize even though
    // the session itself was created successfully.
    var vc = new URLSearchParams(window.location.search).get('vc')
    var attempts = 0
    while (attempts < 2) {
      try {
        var res = await this.$api.post(
          '/shared-cart-sessions/' + state.sessionId + '/mutations',
          vc ? { actions: mutateActions, vc: vc } : { actions: mutateActions }
        )
        var responseData = res.data || {}
        var order = getOrderFromResponse(responseData)
        commit('SET_SESSION', {
          sessionId: getSessionIdFromResponse(responseData) || state.sessionId,
          storeId: responseData.storeId || state.storeId,
          tableId: responseData.tableId || responseData.tableNo || state.tableId,
          status: responseData.status || state.status,
          cartVersion: responseData.cartVersion,
          order: order
        })
        syncOrderToCartStore(this, order)
        return responseData
      } catch (error) {
        if (isSessionClosedError(error)) {
          await dispatch('refetchAndMerge').catch(function() {})
          await dispatch('clearSharedCart')
          commit('SET_ERROR', error)
          showMutateCartErrorSnack(this, error)
          throw error
        }
        if (isItemVersionConflictError(error)) {
          // Retrying with the same expectedItemVersion would just conflict
          // again — refetch so the UI shows the current item state, then
          // surface the failure instead of silently giving up.
          await dispatch('refetchAndMerge').catch(function() {})
          commit('SET_ERROR', error)
          showMutateCartErrorSnack(this, error)
          throw error
        }
        if (isRetryableConflictError(error) && attempts === 0) {
          await dispatch('refetchAndMerge')
          attempts++
          continue
        }
        commit('SET_ERROR', error)
        showMutateCartErrorSnack(this, error)
        throw error
      }
    }
  },

  submitRound: async function({ commit, state, dispatch }, payload) {
    payload = payload || {}
    if (!state.sessionId) return null
    await refuseInvalidDraft(dispatch, state)
    if (!state.sessionId) throw sessionClosedError()
    commit('SET_ERROR', null)
    // See mutateCart's comment: dynamic QR stores re-validate vc on every write.
    var vc = new URLSearchParams(window.location.search).get('vc')
    try {
      var res = await this.$api.post(
        '/shared-cart-sessions/' + state.sessionId + '/rounds/fire',
        vc ? { expectedCartVersion: state.cartVersion, vc: vc } : { expectedCartVersion: state.cartVersion }
      )
      var responseData = res.data || {}
      var order = getOrderFromResponse(responseData)
      commit('SET_SESSION', {
        sessionId: getSessionIdFromResponse(responseData) || state.sessionId,
        storeId: responseData.storeId || state.storeId,
        tableId: responseData.tableId || responseData.tableNo || state.tableId,
        status: responseData.status || state.status,
        cartVersion: responseData.cartVersion,
        order: order
      })
      syncOrderToCartStore(this, order)
      return responseData
    } catch (error) {
      if (isSessionClosedError(error)) {
        await dispatch('refetchAndMerge').catch(function() {})
        await dispatch('clearSharedCart')
      } else if (error.response && error.response.status === 409) {
        await dispatch('refetchAndMerge')
      }
      commit('SET_ERROR', error)
      throw error
    }
  },

  restoreSession: async function({ dispatch, state }, wsHandlers) {
    if (state.sessionId && state.order) return true

    var sessionId = lsGet(LS_KEYS.sessionId)
    var storeId = lsGet(LS_KEYS.storeId)
    var tableId = lsGet(LS_KEYS.tableId)
    if (!sessionId || !storeId || !tableId) return false

    // An explicit ?sc=<id> in the URL always names the session the user
    // intends to join right now — e.g. a fresh invite link for a NEW shared
    // table, opened on a device that still has an older (possibly still-
    // active) session persisted in localStorage from a previous table. Don't
    // silently keep the old one just because it happened to load first;
    // defer to whatever the URL-driven join flow (MenuSelectionPage) decides.
    var routeQuery = (this.$router && this.$router.currentRoute && this.$router.currentRoute.query) || {}
    var urlSessionId = routeQuery.sc || routeQuery.sharedCartSessionId
    if (urlSessionId && urlSessionId !== sessionId) {
      return false
    }

    try {
      await dispatch('configureSession', { sessionId: sessionId, storeId: storeId, tableId: tableId, wsHandlers: wsHandlers || {} })
      await dispatch('refetchAndMerge')
      // refetchAndMerge clears local state internally (without throwing) when
      // it discovers the session has already ended — nothing else signals
      // that here, so check we're still actually active before reporting a
      // successful restore. Otherwise callers show "reconnected"/"joined"
      // dialogs for a table that's already closed.
      return !!state.sessionId
    } catch (e) {
      dispatch('clearSharedCart')
      return false
    }
  },

  connectWs: function({ state }, handlers) {
    if (!state.sessionId || !state.storeId || !state.tableId) return
    if (_wsInstance) {
      _wsInstance.disconnect()
    }
    _wsInstance = new SharedCartWebSocketService({
      storeId: state.storeId,
      tableId: state.tableId,
      store: this,
      router: this.$router,
      handlers: handlers || {}
    })
    _wsInstance.connect()
  },

  disconnectWs: function() {
    if (_wsInstance) {
      _wsInstance.disconnect()
      _wsInstance = null
    }
  },

  markSessionDead: function(_, sessionId) {
    if (sessionId) _knownDeadSessionIds.add(sessionId)
  },

  clearSharedCart: function({ commit, dispatch }) {
    dispatch('disconnectWs')
    clearPersistedSession()
    commit('RESET_SHARED_CART')
    // syncOrderToCartStore mirrors the shared-cart order into the regular cart
    // module's state (localCart/serverCart/subOrder) so Cart.vue can render it.
    // Once the shared session ends, that mirrored state must be cleared too —
    // otherwise the regular checkout page picks up the shared table's items as
    // if they were a normal, freely-editable local cart (like group order, the
    // shared session should leave no trace in the regular cart once it's over).
    commit('RESET_CART', { storeId: null, tempSlots: {} }, { root: true })
  },

  finalizeSession: async function({ commit, state, dispatch }, payload) {
    payload = payload || {}
    if (!state.sessionId) return null
    // Finalize also fires whatever is still in the draft -- same guard as submitRound.
    await refuseInvalidDraft(dispatch, state)
    if (!state.sessionId) throw sessionClosedError()
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    // See mutateCart's comment: dynamic QR stores re-validate vc on every write.
    var vc = new URLSearchParams(window.location.search).get('vc')
    var attempts = 0
    try {
      while (attempts < 2) {
        try {
          var body = {
            expectedCartVersion: state.cartVersion,
            email: payload.email || null,
            tipsAmount: payload.tipsAmount || 0,
            tipsForStore: payload.tipsForStore || 0,
            estimatedTotal: payload.estimatedTotal || null,
            kioskMode: payload.kioskMode || false
          }
          if (vc) body.vc = vc
          var res = await this.$api.post('/shared-cart-sessions/' + state.sessionId + '/finalize', body)
          var responseData = res.data || {}
          dispatch('clearSharedCart')
          return responseData
        } catch (error) {
          if (isSessionClosedError(error)) {
            // Someone else already finalized/cancelled the session — nothing
            // to retry, just clear our stale local state.
            await dispatch('refetchAndMerge').catch(function() {})
            await dispatch('clearSharedCart')
            commit('SET_ERROR', error)
            throw error
          }
          if (isRetryableConflictError(error) && attempts === 0) {
            // Our cartVersion was stale (another guest's action landed first) —
            // refetch the current version and retry once with the correct one.
            await dispatch('refetchAndMerge')
            attempts++
            continue
          }
          commit('SET_ERROR', error)
          throw error
        }
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  handleCancelled: function({ commit, dispatch, state }) {
    // SESSION_CANCELLED isn't always a true cancellation — the backend also
    // publishes it from closeSessionAsPending() when a finalize's payment
    // step fails AFTER the bill already reached the POS terminal, and from
    // the merchant-clears-table hook when an order is still pending. Both of
    // those mean "pay at the counter", not "your session was cancelled".
    // Capture orderStatus BEFORE clearSharedCart wipes state.order, and let
    // App.vue's existing sessionClosedNotice watcher pick the right dialog —
    // same pattern refetchAndMerge already uses for the equivalent case
    // discovered via a non-WS refetch instead of a live event.
    var orderStatus = state.order && state.order.orderStatus
    if (orderStatus === 'PAY_REQUEST' || orderStatus === 'PAID' || orderStatus === 'COMPLETED') {
      commit('SET_SESSION_CLOSED_NOTICE', { orderStatus: orderStatus })
      dispatch('clearSharedCart')
      return
    }
    dispatch('clearSharedCart')
    commit('SET_CANCELLED', true)
  }
}

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions
}
